import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { IPagedResult } from '../shared/models/PagedResult';
import { APP_CONFIG } from '../app-config.token';
import { AppConfig } from '../app-config';
import { Filter } from '../models/common/filter';
import { EFolderGridItemDto } from '../models/dtos/e-folder-grid-item-dto';
import { EAbsenceConclusionGridItemDto } from '../models/dtos/e-absence-conclusion-grid-item-dto';
import { CreateEDocumentResponse } from '../models/results/create-e-document-response';
import { EFolderValidationResultDto } from '../models/dtos/e-folder-validation-result-dto';
import { EDocumentGridItemDto } from '../models/dtos/e-document-grid-item-dto';
import { EDocumentPermissionDto } from '../models/dtos/e-document-permission-dto';

export interface EDocRow {
  id: string;
  docType: string;          // np. "Umowa", "Aneks", "BHP"
  title: string;
  validFrom?: string;       // ISO
  validTo?: string;         // ISO
  retentionNote?: string;   // tooltip np. „retencja do 2035-12-31”
  signatureStatusName: string; // np. "Podpisany", "Do podpisu"
  signatureIssues?: string[];
  version: string;
  addedBy: string;
  createdOn: string;        // ISO
  modifiedOn?: string;      // ISO
}

export interface LoadQuery {
  sortBy: string;
  sortDir: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

@Injectable()
export class EDocumentationService {
  private readonly API_URL = this.config.resourceApiURI;
  private readonly path = '/e-documentations';

  private get url() {
    return this.API_URL + this.path;
  }

  constructor(
    private http: HttpClient,
    @Inject(APP_CONFIG)
    private config: AppConfig
  ) { }

  getAllDocuments(
    page: number,
    count: number,
    sortingField: string,
    sortingDirection: string = 'desc',
    filters: Filter[],
  ): Observable<IPagedResult<EDocumentGridItemDto>> {
    const url = `${this.url}/documents`;

    let params = new HttpParams().set('page', `${page || 1}`).set('count', `${count || 10}`);

    if (sortingField) {
      params = params.set('sortingField', sortingField).set('sortingDirection', sortingDirection);
    }

    if (filters) {
      params = params.set('filterString', JSON.stringify(filters));
    }

    return this.http.get<IPagedResult<EDocumentGridItemDto>>(url, { params }).pipe(
      map(
        (pagedResult) => {
          return {
            ...pagedResult,
            Results: pagedResult.Results?.map(
              (item) => ({
                ...item,
                AddedDate: item.AddedDate && new Date(item.AddedDate + 'Z'),
                ValidUntilDate: item.ValidUntilDate && new Date(item.ValidUntilDate + 'Z')
              })
            )
          };
        }
      )
    );
  }

  getAllFolders(
    page: number,
    count: number,
    sortingField: string,
    sortingDirection: string = 'desc',
    filters: Filter[],
  ): Observable<IPagedResult<EFolderGridItemDto>> {
    const url = `${this.url}/folders`;

    let params = new HttpParams().set('page', `${page || 1}`).set('count', `${count || 10}`);

    if (sortingField) {
      params = params.set('sortingField', sortingField).set('sortingDirection', sortingDirection);
    }

    if (filters) {
      params = params.set('filterString', JSON.stringify(filters));
    }

    return this.http.get<IPagedResult<EFolderGridItemDto>>(url, { params }).pipe(
      map(
        (pagedResult) => {
          return {
            ...pagedResult,
            Results: pagedResult.Results?.map(
              (item) => ({
                ...item,
                AddedDate: item.AddedDate && new Date(item.AddedDate + 'Z'),
                ValidUntilDate: item.ValidUntilDate && new Date(item.ValidUntilDate + 'Z')
              })
            )
          };
        }
      )
    );
  }

  getAllAbsenceConclusions(
    page: number,
    count: number,
    sortingField: string,
    sortingDirection: string = 'desc',
    filters: Filter[],
  ): Observable<IPagedResult<EAbsenceConclusionGridItemDto>> {
    const url = `${this.url}/absence-conclusions`;

    let params = new HttpParams().set('page', `${page || 1}`).set('count', `${count || 10}`);

    if (sortingField) {
      params = params.set('sortingField', sortingField).set('sortingDirection', sortingDirection);
    }

    if (filters) {
      params = params.set('filterString', JSON.stringify(filters));
    }

    return this.http.get<IPagedResult<EAbsenceConclusionGridItemDto>>(url, { params }).pipe(
      map(
        (pagedResult) => {
          return {
            ...pagedResult,
            Results: pagedResult.Results?.map(
              (item) => ({
                ...item,
                StartDate: item.StartDate && new Date(item.StartDate + 'Z'),
                EndDate: item.EndDate && new Date(item.EndDate + 'Z')
              })
            )
          };
        }
      )
    );
  }

  createEDocument(request: EFolderGridItemDto) {
    const url = `${this.url}/document`;
    return this.http.post<CreateEDocumentResponse>(url, request);
  }

  updateEDocument(request: EFolderGridItemDto) {
    const url = `${this.url}/document`;
    return this.http.put<number>(url, request);
  }

  saveFile(base64: string): Observable<{ FileName: string }> {
    const url = `${this.url}/saveFile`;
    return this.http.post<{ FileName: string }>(url, { Base64: base64 });
  }

  getAllDraftFolders(
    page: number,
    count: number,
    sortingField: string,
    sortingDirection: string = 'desc',
    filters: Filter[],
  ): Observable<IPagedResult<any>> {
    const url = `${this.url}/draftFolders`;

    let params = new HttpParams().set('page', `${page || 1}`).set('count', `${count || 10}`);

    if (sortingField) {
      params = params.set('sortingField', sortingField).set('sortingDirection', sortingDirection);
    }

    if (filters) {
      params = params.set('filterString', JSON.stringify(filters));
    }

    return this.http.get<IPagedResult<any>>(url, { params }).pipe(
      map(
        (pagedResult) => {
          return {
            ...pagedResult,
            Results: pagedResult.Results?.map(
              (item) => ({
                ...item,
                AddedDate: item.AddedDate && new Date(item.AddedDate + 'Z'),
                ValidUntilDate: item.ValidUntilDate && new Date(item.ValidUntilDate + 'Z')
              })
            )
          };
        }
      )
    );
  }

  getAllUcpFolders(
    page: number,
    count: number,
    sortingField: string,
    sortingDirection: string = 'desc',
    filters: Filter[],
  ): Observable<IPagedResult<any>> {
    const url = `${this.url}/ucpFolders`;

    let params = new HttpParams().set('page', `${page || 1}`).set('count', `${count || 10}`);

    if (sortingField) {
      params = params.set('sortingField', sortingField).set('sortingDirection', sortingDirection);
    }

    if (filters) {
      params = params.set('filterString', JSON.stringify(filters));
    }

    return this.http.get<IPagedResult<any>>(url, { params }).pipe(
      map(
        (pagedResult) => {
          return {
            ...pagedResult,
            Results: pagedResult.Results?.map(
              (item) => ({
                ...item,
                AddedDate: item.AddedDate && new Date(item.AddedDate + 'Z'),
                ValidUntilDate: item.ValidUntilDate && new Date(item.ValidUntilDate + 'Z')
              })
            )
          };
        }
      )
    );
  }

  getValidationResult(id: number, statusId: number): Observable<EFolderValidationResultDto[]> {
    const url = `${this.url}/validationResult/${id}`;

    let params = new HttpParams().set('statusId', statusId)

    return this.http.get<EFolderValidationResultDto[]>(url, { params });
  }

  getEDocumentPermission(): Observable<EDocumentPermissionDto[]> {
    const url = `${this.url}/documentPermissions`;

    return this.http.get<EDocumentPermissionDto[]>(url);
  }

  saveEDocumentPermission(request: EDocumentPermissionDto) {
    const url = `${this.url}/documentPermissions`;
    return this.http.post<any>(url, request);
  }

  getEDocumentsByWorkerId(workerId: number): Observable<any[]> {
    const url = `${this.url}/${workerId}/documents`;

    return this.http.get<any[]>(url).pipe(
      map(
        (res) => {
          return res?.map(
            (item) => ({
              ...item,
              AddedDate: item.AddedDate && new Date(item.AddedDate + 'Z'),
              ValidUntilDate: item.ValidUntilDate && new Date(item.ValidUntilDate + 'Z')
            })
          )
        }));
  }



  private _rows = new BehaviorSubject<EDocRow[]>([]);
  private _total = new BehaviorSubject<number>(0);

  rows$ = this._rows.asObservable();
  total$ = this._total.asObservable();

  load(q: LoadQuery): void {
    // TODO: podłącz do backendu: GET /employees/{id}/edossier/documents?part=B
    // tu stub pod UI
    const mock: EDocRow[] = [
      {
        id: '1',
        docType: 'Umowa o pracę',
        title: 'Umowa – stanowisko X',
        validFrom: '2024-01-01',
        validTo: '2024-12-31',
        signatureStatusName: 'Podpisany',
        version: 'v1',
        addedBy: 'HR',
        createdOn: '2024-01-02',
        modifiedOn: '2024-04-10'
      },
      {
        id: '2',
        docType: 'Aneks',
        title: 'Aneks – podwyżka',
        validFrom: '2024-05-01',
        retentionNote: 'Retencja do 2035-01-01',
        signatureStatusName: 'Do podpisu',
        signatureIssues: ['Brak znacznika czasu TSP'],
        version: 'v2',
        addedBy: 'HR',
        createdOn: '2024-05-01'
      }
    ];
    this._rows.next(mock);
    this._total.next(2);
  }

  addDocument(): void { /* otwórz wizard uploadu */ }
  preview(row: EDocRow): void { /* podgląd PDF.js */ }
  download(row: EDocRow): void { /* strumień */ }
  edit(row: EDocRow): void { /* panel metadanych */ }
  sign(row: EDocRow): void { /* wywołanie podpisu/TSP */ }
  share(row: EDocRow): void { /* kreator paczki */ }
}
